import { useMemo, useState, useEffect, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { DataGrid, KeenIcon } from '@/components';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import usePagination from '@/hooks/usePagination';
import { ErrorApiResponse } from '@/types/api.types';
import { ModerationState } from '@/types/service-management.types';
import { formatDisplayDate } from '@/utils/Date';
import {
  useGetModerationServicesQuery,
  useModerateServiceMutation,
  ModerationListItem,
  useGetModerationCountsQuery,
} from '@/services/ServiceModerationService';

// Listings fetched from API

const ServiceModerationTab = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
  } = usePagination();

  const [statusFilter, setStatusFilter] = useState<ModerationState>(
    ModerationState.SUBMITTED
  );
  const {
    data: countsData,
    isLoading: isCountsLoading,
    refetch: refetchCounts,
  } = useGetModerationCountsQuery();

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    showReasonInput?: boolean;
    actionType?: 'approve' | 'reject';
    serviceId?: string;
  }>({
    open: false,
    title: '',
    message: '',
  });

  const handleViewDetails = useCallback(
    (id: string) => {
      navigate(`/service-moderation/details/${id}`);
    },
    [navigate]
  );

  const { data, isLoading, isFetching, isError, refetch } =
    useGetModerationServicesQuery({
      moderation_state: statusFilter,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedValue,
    });

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.totalItems || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  const handleApprove = (id: string) => {
    setConfirmModal({
      open: true,
      title: 'Approve Service',
      message:
        'Are you sure you want to approve this service? It will become visible to users.',
      actionType: 'approve',
      serviceId: id,
      showReasonInput: false,
    });
  };

  const handleReject = (id: string) => {
    setConfirmModal({
      open: true,
      title: 'Reject Service',
      message:
        'Please provide a reason for rejecting this service. This will be shared with the provider.',
      actionType: 'reject',
      serviceId: id,
      showReasonInput: true,
    });
  };

  const [moderateService, { isLoading: isModerating }] =
    useModerateServiceMutation();

  const onConfirmAction = async (reason?: string) => {
    const id = confirmModal.serviceId as string;
    try {
      if (confirmModal.actionType === 'approve') {
        await moderateService({
          id,
          payload: { moderation_state: ModerationState.APPROVED },
        }).unwrap();
        enqueueSnackbar('Service approved successfully', {
          variant: 'success',
        });
      } else {
        if (!reason || reason.trim().length < 10) {
          enqueueSnackbar('Reason must be at least 10 characters.', {
            variant: 'warning',
          });
          return;
        }
        await moderateService({
          id,
          payload: {
            moderation_state: ModerationState.REJECTED,
            moderation_reason: reason.trim(),
          },
        }).unwrap();
        enqueueSnackbar('Service rejected successfully');
      }
      setConfirmModal({ ...confirmModal, open: false });
      refetch();
      refetchCounts();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse)?.data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const getStatusBadge = (state: ModerationState) => {
    switch (state) {
      case ModerationState.SUBMITTED:
        return <span className="badge badge-info">Submitted</span>;
      case ModerationState.APPROVED:
        return <span className="badge badge-success">Approved</span>;
      case ModerationState.REJECTED:
        return <span className="badge badge-danger">Rejected</span>;
      case ModerationState.DISABLED:
        return <span className="badge">Disabled</span>;
      default:
        return <span className="badge badge-secondary">N/A</span>;
    }
  };

  // Chip styles for the status filter pills
  const chipClassMap: Record<
    ModerationState,
    { active: string; inactive: string; dot: string }
  > = {
    [ModerationState.DRAFT]: {
      active:
        'bg-gray-600 text-white border-gray-600 ' +
        'dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700 ' +
        'contrast:bg-gray-950 contrast:text-gray-50 contrast:border-gray-400',
      inactive:
        'text-gray-700 bg-gray-50 border-gray-300 hover:bg-gray-100 ' +
        'dark:text-gray-200 dark:bg-gray-900 dark:border-gray-700 ' +
        'contrast:text-gray-50 contrast:bg-gray-950 contrast:border-gray-400',
      dot: 'bg-gray-600 dark:bg-gray-400 contrast:bg-gray-300',
    },

    [ModerationState.SUBMITTED]: {
      active:
        'bg-blue-600 text-white border-blue-600 ' +
        'dark:bg-blue-900 dark:text-blue-100 dark:border-blue-800 ' +
        'contrast:bg-blue-900 contrast:text-blue-50 contrast:border-blue-400',
      inactive:
        'text-blue-700 bg-blue-50 border-blue-300 hover:bg-blue-100 ' +
        'dark:text-blue-300 dark:bg-blue-950 dark:border-blue-800 ' +
        'contrast:text-blue-50 contrast:bg-blue-950 contrast:border-blue-400',
      dot: 'bg-blue-600 dark:bg-blue-400 contrast:bg-blue-300',
    },

    [ModerationState.APPROVED]: {
      active:
        'bg-green-600 text-white border-green-600 ' +
        'dark:bg-green-900 dark:text-green-100 dark:border-green-800 ' +
        'contrast:bg-green-900 contrast:text-green-50 contrast:border-green-400',
      inactive:
        'text-green-700 bg-green-50 border-green-300 hover:bg-green-100 ' +
        'dark:text-green-300 dark:bg-green-950 dark:border-green-800 ' +
        'contrast:text-green-50 contrast:bg-green-950 contrast:border-green-400',
      dot: 'bg-green-600 dark:bg-green-400 contrast:bg-green-300',
    },

    [ModerationState.REJECTED]: {
      active:
        'bg-red-600 text-white border-red-600 ' +
        'dark:bg-red-900 dark:text-red-100 dark:border-red-800 ' +
        'contrast:bg-red-900 contrast:text-red-50 contrast:border-red-400',
      inactive:
        'text-red-900 bg-red-100 border-red-300 hover:bg-red-200 ' +
        'dark:text-red-300 dark:bg-red-950 dark:border-red-800 ' +
        'contrast:text-red-50 contrast:bg-red-950 contrast:border-red-400',
      dot: 'bg-red-600 dark:bg-red-400 contrast:bg-red-300',
    },

    [ModerationState.DISABLED]: {
      active:
        'bg-amber-600 text-white border-amber-600 ' +
        'dark:bg-amber-900 dark:text-amber-100 dark:border-amber-800 ' +
        'contrast:bg-amber-900 contrast:text-amber-50 contrast:border-amber-400',
      inactive:
        'text-amber-700 bg-amber-50 border-amber-300 hover:bg-amber-100 ' +
        'dark:text-amber-300 dark:bg-amber-950 dark:border-amber-800 ' +
        'contrast:text-amber-50 contrast:bg-amber-950 contrast:border-amber-400',
      dot: 'bg-amber-600 dark:bg-amber-400 contrast:bg-amber-300',
    },
  } as const;

  const handleResetFilters = () => {
    setStatusFilter(ModerationState.SUBMITTED);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    const resetEvent = {
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>;
    handleSearch(resetEvent);
  };

  const columns = useMemo<ColumnDef<ModerationListItem>[]>(
    () => [
      {
        accessor: (row: ModerationListItem) => row.name,
        id: 'name',
        header: 'Service Name',
        cell: ({ row }) => (
          <button
            onClick={() => handleViewDetails(row.original.id)}
            className="text-start hover:text-primary transition-colors duration-200 focus:outline-none"
          >
            <span className="font-medium text-gray-900 block">
              {row.original.name}
            </span>
          </button>
        ),
      },
      {
        accessor: (row: ModerationListItem) => row.owner_name,
        id: 'owner',
        header: 'Owner',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <span className="font-medium text-gray-900">
              {row.original.owner_name}
            </span>
            <span
              className={clsx(
                'text-2xs uppercase font-semibold px-1.5 py-0.5 rounded-md w-fit mt-1',
                row.original.owner_type === 'HARBOUR'
                  ? 'bg-blue-100 text-blue-600 contrast:bg-blue-950 contrast:text-blue-200'
                  : 'bg-purple-100 text-purple-600'
              )}
            >
              {row.original.owner_type === 'HARBOUR'
                ? 'Harbor'
                : 'Service Provider'}
            </span>
          </div>
        ),
      },
      {
        accessor: (row: ModerationListItem) => row.moderation_state,
        id: 'moderation_state',
        header: 'Status',
        cell: ({ row }: { row: { original: ModerationListItem } }) => (
          <div className="flex flex-col gap-1">
            {getStatusBadge(row.original.moderation_state)}
            {(row.original.moderation_state === ModerationState.REJECTED ||
              row.original.moderation_state === ModerationState.DISABLED) &&
              row.original.moderation_reason &&
              (() => {
                const fullReason = row.original.moderation_reason as string;
                const maxLength = 50;
                const displayReason =
                  fullReason.length > maxLength
                    ? `${fullReason.slice(0, maxLength)}...`
                    : fullReason;

                return (
                  <span
                    className="text-[10px] text-red-500 italic max-w-[150px]"
                    title={fullReason}
                  >
                    Reason: {displayReason}
                  </span>
                );
              })()}
          </div>
        ),
      },
      {
        accessor: (row: ModerationListItem) =>
          row.submitted_at || row.created_at,
        id: 'submitted_at',
        header: 'Submitted Date',
        cell: ({ row }) =>
          formatDisplayDate(new Date(
            row.original.submitted_at || row.original.created_at
          )),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              className="btn btn-sm btn-icon btn-light btn-outline"
              onClick={() => handleViewDetails(row.original.id)}
              title="View Details"
            >
              <KeenIcon type="filled" icon="eye" />
            </button>
            {row.original.moderation_state === ModerationState.SUBMITTED && (
              <>
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleApprove(row.original.id)}
                  title="Approve"
                >
                  <KeenIcon icon="check" className="text-success" />
                </button>
                <button
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  onClick={() => handleReject(row.original.id)}
                  title="Reject"
                >
                  <KeenIcon
                    type="filled"
                    icon="cross"
                    className="text-danger"
                  />
                </button>
              </>
            )}
          </div>
        ),
      },
    ],
    [handleViewDetails]
  );

  // Server-side pagination; no client filtering

  return (
    <>
      <div className="card card-grid min-w-full">
        <div className="card-header">
          <div className="w-full rounded-xl border border-blue-100 dark:border-blue-800 contrast:border-blue-800 bg-blue-50 dark:bg-blue-950 contrast:bg-blue-950 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-white dark:bg-blue-900 contrast:bg-blue-900 flex items-center justify-center border border-blue-100  dark:border-blue-800 contrast:border-blue-800">
                  <KeenIcon
                    icon="filter"
                    className="text-blue-600 dark:text-blue-50 contrast:text-blue-50"
                  />
                </div>
                <div>
                  <div className="font-semibold text-blue-900 dark:text-blue-50 contrast:text-blue-50">
                    Filters & Search
                  </div>
                  <div className="text-2xs text-blue-700 dark:text-blue-200 contrast:text-blue-200">
                    Customize your moderation view
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-xs btn-light"
                onClick={handleResetFilters}
              >
                <KeenIcon icon="cross" className="mr-1" /> Reset All
              </button>
            </div>

            <div className="mt-4">
              <div className="text-2xs font-semibold text-gray-700 mb-2">
                Status Filters
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {(
                  [
                    { key: ModerationState.SUBMITTED, label: 'Submitted' },
                    { key: ModerationState.APPROVED, label: 'Approved' },
                    { key: ModerationState.REJECTED, label: 'Rejected' },
                    { key: ModerationState.DISABLED, label: 'Disabled' },
                  ] as const
                ).map(({ key, label }) => {
                  const active = statusFilter === key;
                  const classes = clsx(
                    'px-3 py-1.5 rounded-full border text-sm font-medium flex items-center gap-2 transition',
                    active
                      ? chipClassMap[key].active
                      : chipClassMap[key].inactive
                  );
                  const dotClass = clsx(
                    'h-2 w-2 rounded-full',
                    chipClassMap[key].dot,
                    active && 'bg-white'
                  );
                  return (
                    <button
                      key={key}
                      type="button"
                      className={classes}
                      onClick={() => {
                        setStatusFilter(key);
                        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                      }}
                      aria-pressed={active}
                    >
                      <span className={dotClass} />
                      <span>{label}</span>
                      <span
                        className={clsx(
                          'px-2 py-0.5 rounded-full text-2xs border',
                          active
                            ? 'bg-white/20 text-white border-white/30'
                            : 'bg-white contrast:bg-black text-current border-transparent'
                        )}
                      >
                        {isCountsLoading
                          ? '-'
                          : (countsData?.counts?.[key] ?? 0)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4">
              <div className="input input-sm h-10 flex items-center w-full sm:w-80 ">
                <KeenIcon icon="magnifier" className="text-gray-500" />
                <input
                  placeholder="Search by name or owner..."
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  aria-label="Search by name or owner"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="card-body">
          <DataGrid
            columns={columns}
            data={data?.data || []}
            rowSelect={false}
            loading={isLoading || isFetching || isModerating}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
            }}
          />
          {isError && (
            <div className="text-center py-4 text-red-500">
              Failed to load services.
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        open={confirmModal.open}
        onClose={() => setConfirmModal({ ...confirmModal, open: false })}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={onConfirmAction}
        showReasonInput={confirmModal.showReasonInput}
        maxReasonLength={50}
        confirmLabel={
          confirmModal.actionType === 'approve' ? 'Approve' : 'Reject'
        }
        confirmButtonVariant={
          confirmModal.actionType === 'approve' ? 'primary' : 'danger'
        }
        disabled={isModerating}
      />
    </>
  );
};

export default ServiceModerationTab;
